import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  addReportAttachment,
  createBlock,
  createKudos,
  createReport,
  getKudosEligibility,
  listBlocks,
  listReports,
  removeBlock,
} from '../lib/api/safety'
import type {
  CreateBlockRequest,
  CreateKudosRequest,
  CreateReportRequest,
} from '../types/community'

// COTC_SafetyKudos — react-query hooks over the safety api module.

export function useReports(enabled = true) {
  return useQuery({ queryKey: ['community', 'reports'], queryFn: listReports, enabled })
}

export function useBlocks(enabled = true) {
  return useQuery({ queryKey: ['community', 'blocks'], queryFn: listBlocks, enabled })
}

export function useCreateReport() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (req: CreateReportRequest) => createReport(req),
    onSuccess: (_result, request) => {
      qc.invalidateQueries({ queryKey: ['community', 'reports'] })
      qc.invalidateQueries({ queryKey: ['community', 'blocks'] })
      qc.invalidateQueries({ queryKey: ['community', 'me'] })
      if (request.blockRequested) {
        for (const key of ['roster', 'room', 'rooms', 'room-chat', 'group', 'groups']) void qc.invalidateQueries({ queryKey: ['community', key] })
        qc.invalidateQueries({ queryKey: ['community', 'friends'] })
        qc.invalidateQueries({ queryKey: ['community', 'directory'] })
      }
    },
  })
}

export function useAddReportAttachment() {
  return useMutation({ mutationFn: (v: { id: string; file: File }) => addReportAttachment(v.id, v.file) })
}

export function useCreateBlock() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (req: CreateBlockRequest) => createBlock(req),
    onSuccess: () => {
      for (const key of ['blocks', 'me', 'friends', 'directory', 'roster', 'room', 'rooms', 'room-chat', 'group', 'groups']) void qc.invalidateQueries({ queryKey: ['community', key] })
    },
  })
}

export function useRemoveBlock() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => removeBlock(id),
    onSuccess: () => {
      for (const key of ['blocks', 'me', 'friends', 'directory', 'roster', 'room', 'rooms', 'room-chat', 'group', 'groups']) void qc.invalidateQueries({ queryKey: ['community', key] })
    },
  })
}

export function useCreateKudos() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (req: CreateKudosRequest) => createKudos(req),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['community', 'friends'] })
      qc.invalidateQueries({ queryKey: ['community', 'directory'] })
    },
  })
}

/** Imperative eligibility check for opening the kudos modal (six-month guard). */
export function checkKudosEligibility(targetPersonId?: string, targetHandle?: string) {
  return getKudosEligibility(targetPersonId, targetHandle)
}
